"use client";

import React, { useEffect } from "react";
import { useRouter } from "next/navigation";

import { selectLoginStatus, useAppSelector } from "@repo/global-store";
import { APP_PROMPTS } from "../../constants/pwa.constants";
import { getSSOConfigsFromUrl, ssoRedirect } from "@repo/sso/redirect-utils";
import { isNextPage } from "../../utils/common-utils";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { isAmcClpPage } from "./amcClpPageUtils";
import { shouldStopPropagationForHeaderSsoLink } from "./headerSsoLinkUtils";

/**
 * Global document-level click handler for anchor navigation, SSO redirects, and analytics.
 * Stops duplicate navigation from header onClick handlers + global ClickHandler.
 * On the AMC CLP page, skips legacy dataLayer CT so amc-clp-libs owns click analytics.
 */
export default function ClickHandler() {
  const loginStatus = useAppSelector(selectLoginStatus);
  const router = useRouter();

  const getNearestAnchorElement = (element: HTMLElement): HTMLAnchorElement | null => {
    if (element.tagName === "A") {
      return element as HTMLAnchorElement;
    }
    const anchorElement = element.closest("a");
    if (anchorElement) {
      return anchorElement;
    }
    return null;
  };

  const handleHrefClick = async (e: MouseEvent, anchorElement: HTMLAnchorElement) => {
    const href = anchorElement.href.toLowerCase();
    e.preventDefault();
    if (href.includes(APP_PROMPTS.BAJAJ_SUPER_APP)) {
      e.preventDefault();
      const windowWithTarsier = window as Window & {
        tarsier?: {
          invokePrompt: (prompt: string, force?: boolean) => unknown;
        };
      };
      windowWithTarsier.tarsier?.invokePrompt(
        `${APP_PROMPTS.IN_APP_REDIRECTION}${anchorElement.href}`,
        true
      );
      return;
    }

    const mapping = getSSOConfigsFromUrl(anchorElement.href);
    const relativeHref = anchorElement.getAttribute("href") ?? anchorElement.href;
    const openInSameTab = shouldStopPropagationForHeaderSsoLink(anchorElement.href);
    if (mapping?.ssoModule) {
      ssoRedirect(
        {
          href: anchorElement.href,
          target: openInSameTab ? "_self" : anchorElement.target,
          checkCart: true,
          showResumePopup: true,
        },
        loginStatus,
        mapping
      );
      return;
    } else {
      try {
        const dlValue = JSON.parse(anchorElement.getAttribute("data-dl") || "{}");
        const ctValue = JSON.parse(anchorElement.getAttribute("data-ct") || "{}");
        const skipAnalytics = isAmcClpPage(window.location.pathname);
        if (!skipAnalytics) {
          dataLayer(loginStatus, dlValue, ctValue);
        }
      } catch (err) {
        console.error("Error parsing ct/dl props:", err);
      }
      if (!isNextPage(relativeHref) || anchorElement.target === "_blank") {
        window.open(anchorElement.href, anchorElement.target || "_self");
      } else {
        router.push(anchorElement.href);
      }
    }
  };

  const hasRunRef = React.useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    if (loginStatus !== "pending") {
      document?.addEventListener(
        "click",
        (e) => {
          const target = e.target as HTMLElement;
          const anchorElement = getNearestAnchorElement(target);
          if (!anchorElement) return;

          if (anchorElement.hasAttribute("data-clp-redirection")) return;

          const href = anchorElement.href;
          if (!href) return;

          if (shouldStopPropagationForHeaderSsoLink(href)) {
            e.stopPropagation();
          }

          queueMicrotask(() => handleHrefClick(e, anchorElement));
        },
        { capture: true }
      );
      hasRunRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- attach global listener once after login status resolves
  }, [loginStatus]);

  return null;
}
